/** The chain, from the browser's side — Phase F's *"ledger UI and auditor role end to end"*.
 *
 * Three things worth stating here rather than in the component.
 *
 * **The cursor is a URL parameter, not component state.** `before` is the exclusive sequence a page
 * ends at, and a page of the chain is a thing an auditor sends somebody — the same reason the
 * receipt drawer is addressed by URL (ADR-0027, and `32-information-architecture.md`). It also means
 * the browser's Back button is the "newer entries" control, which is why there is no `after`: the
 * API has no cursor in that direction and inventing one on the client would be a second answer to
 * where a reader is in the chain.
 *
 * **One page is in the document at a time.** The register's lesson (EXP-39) was that rows cost
 * 12 nodes each and the cost is paid again on every re-render; a chain grows without bound and has
 * no filter to narrow it, so accumulating pages would be the same defect with no ceiling at all.
 * Every entry is still reachable, by following `next_before_seq` — which is a real cursor because
 * `seq` is dense, monotonic and never deleted.
 *
 * **A failed read is never an empty chain.** `unwrap` throws a `Refused` carrying the status and
 * the code, so the screen can tell *"your engagement cannot be applied to a chain"* (403,
 * `chain_has_no_period`) from *"that could not be loaded"*. Nine hooks in this app once wrote
 * `data ?? []` and a dropped request rendered as a confident statement about the company; on this
 * screen it would render as a workspace that has recorded nothing.
 */

import { useQuery } from "@tanstack/react-query";

import { unwrap } from "./queries";
import { api } from "./client";
import type { components } from "./schema";

/** Aliased from the generated schema rather than restated. `evidence.ts` says why in its own
 * words: a restated type is a second source of truth, and the one that drifts is the one nobody
 * regenerates. Two of its hand-written shapes were already subtly wrong when it was converted. */
export type ChainPage = components["schemas"]["ChainPageOut"];
export type ChainEntry = components["schemas"]["ChainEntryOut"];
export type ChainCheck = components["schemas"]["ChainCheckOut"];

export const ledgerKeys = {
  /** Keyed by the cursor, so paging back and forward through the browser's history is instant and
   * the newest page is still refetched on mount — an entry appended while somebody reads is the
   * one thing this screen must not miss. */
  page: (before: number | undefined, limit: number) => ["ledger", before ?? "head", limit] as const,
};

/** How many entries a page asks for. Below the API's own ceiling of 500 on purpose: the page is
 * what a reader looks at, and the ceiling is what the server will do in one request. */
export const PAGE_SIZE = 50;

/** One page of the chain, and the check over it.
 *
 * **Refetched on mount**, like every other read that can change. A receipt is immutable and may be
 * cached forever (`useReceipt`); a chain's head moves every time anybody does anything, and a stale
 * "nothing since Tuesday" is the kind of wrong this screen cannot afford.
 */
export function useChain(before: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ledgerKeys.page(before, PAGE_SIZE),
    enabled,
    queryFn: async (): Promise<ChainPage> =>
      unwrap(
        await api.GET("/api/v1/ledger", {
          params: { query: { limit: PAGE_SIZE, ...(before === undefined ? {} : { before_seq: before }) } },
        }),
      ),
  });
}

/** Whether a page's sequences are consecutive — **checked on the client, deliberately.**
 *
 * The register learned that a cap is only honest while something the reader cannot see stays true:
 * it ordered by tonnage on the server, so *"the rows you cannot reach are the smallest"* was *"a
 * fact on the server that nothing on the client could check"*. Here the ordering is the sequence,
 * and the chain has no gaps — `verify_chain` refuses one, so that a deleted row cannot hide. So the
 * reader can check the page is whole by looking at it, and this is that check rather than a boolean
 * the server asserts about itself.
 *
 * Today it cannot be false: `ledger_entry` is insert-only at the database level and the route
 * filters on nothing but the organisation and the cursor. It is here because the one change that
 * would make it false — a filter, a type selector, a "show only corrections" — is the change that
 * looks like a convenience, and it would turn a chain into a list that cannot be verified.
 */
export function gapsIn(rows: readonly ChainEntry[]): readonly number[] {
  const missing: number[] = [];
  for (let index = 1; index < rows.length; index += 1) {
    const newer = rows[index - 1];
    const older = rows[index];
    // `noUncheckedIndexedAccess` is on and is right to be: a loop bound is a claim about a length,
    // and this one is read from the same array it indexes.
    if (!newer || !older) continue;
    for (let seq = older.seq + 1; seq < newer.seq; seq += 1) missing.push(seq);
  }
  return missing;
}

/** One entry and the one before it — what an entry's own page reads (`/ledger/$seq`).
 *
 * **There is no "entry by sequence" endpoint, and none was added.** The page asks the same
 * `GET /ledger` the list does, with the cursor one past the entry and a limit of two: the first row
 * is the entry, the second is its predecessor. The predecessor is what makes the link checkable from
 * the browser — the entry's `prev_hash` must be the predecessor's `hash` — which is a comparison the
 * reader can see rather than a verdict the server asserts about itself. `check` covers those two
 * entries and says so; it is **not** a verification of the whole chain.
 *
 * `rows[0]` is not trusted to be the entry asked for: if the sequence does not exist (past the head)
 * the first row is some older entry, and the caller compares `seq` rather than assuming.
 */
export function useChainEntry(seq: number, enabled: boolean) {
  return useQuery({
    queryKey: ["ledger", "entry", seq] as const,
    enabled: enabled && Number.isInteger(seq) && seq >= 1,
    queryFn: async (): Promise<ChainPage> =>
      unwrap(
        await api.GET("/api/v1/ledger", { params: { query: { limit: 2, before_seq: seq + 1 } } }),
      ),
  });
}
